// Shared UI atoms: Btn, Modal, Badge, Spinner, Shimmer, squircle bg, PlatformBadge.
// Every new control belongs here, not inline in a page (COMPONENT-FIRST).
import { X, LayoutGrid, List, ArrowUp, ArrowDown } from "lucide-react";
import { C, FONT, WHITE, BLACK, alpha, BRAND } from "../design-system.js";
import { BAZAAR_ICON } from "../brand-marks.js";
import { JALALI_MONTHS, fmt, greToJalaliFull, jalaliMonthDays, toPersianDigits } from "./format.js";

// Small brand badge showing which app a user came from.
// ONE component, one registry — add a platform by adding a row to PLATFORM_MARKS,
// not by adding another branch here.
//
// ⚠️ All of them use their EXACT official marks — never a redrawn imitation (a
// hand-drawn logo silently ships as "the wrong logo", same trap as the
// placeholder crescent-moon PWA icon). Eitaa's mark is ONE evenodd path whose
// outer edge IS the app-icon squircle and whose swirl is a CUT-OUT, so it needs
// a white rounded-rect behind it (rx == the path's own 1024-unit corner radius)
// or the swirl would show the page's dark background through it. Soroush Plus's
// mark is two solid brand-blue (#196FF0) paths on TRANSPARENT — taken verbatim
// from soroushplus.com/assets/images/splus.svg, only rounded to 3 decimals — so
// it likewise sits on a white plate to have any contrast on the dark panel.
// The marks are INLINE on purpose: an <img src="/foo.svg"> would silently break
// into a broken-image glyph the day a deploy ships assets/ without the new file.
// `title` is also the Persian label of the platform wherever one has to be NAMED
// rather than drawn (the platform picker in «افزودن کاربر»), so the registry stays
// the single place a platform is described on this surface.
export const PLATFORM_MARKS = {
  // Bale first: it is the default platform, and it lives in the map like every other
  // one rather than as a special case inside PlatformBadge — otherwise the registry
  // could not answer "which platforms are there?", which the user picker needs.
  bale: {
    title: "بله", label: "Bale",
    render: (size) => (
      <span title="بله" aria-label="Bale"
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox="0 0 32 32" fill={BRAND.bale}>
          <path d="M18.21.16s-.02,0-.03,0c-.24-.03-.48-.06-.72-.08-.08,0-.16-.01-.25-.02-.18-.01-.35-.03-.53-.03-.12,0-.25,0-.37,0-.11,0-.21,0-.31,0-.05,0-.1,0-.14,0-.03,0-.07,0-.1,0-.07,0-.14,0-.21,0-.1,0-.21,0-.31.01-.11,0-.22.01-.33.02-.1,0-.2.01-.31.02-.11,0-.22.02-.33.03-.1.01-.2.02-.3.03-.11.01-.22.03-.33.05-.1.01-.2.03-.3.05-.11.02-.22.04-.32.06-.1.02-.2.04-.29.06-.11.02-.21.05-.32.07-.1.02-.19.04-.29.07-.11.03-.21.06-.32.08-.09.03-.19.05-.28.08-.11.03-.21.06-.31.1-.09.03-.18.06-.27.09-.1.03-.21.07-.31.11-.09.03-.18.06-.27.1-.1.04-.2.08-.31.12-.09.03-.17.07-.26.11-.1.04-.2.09-.31.14-.08.04-.17.07-.25.11-.1.05-.2.1-.3.15-.08.04-.16.08-.24.12-.1.05-.2.11-.3.16-.08.04-.16.08-.23.13-.1.06-.2.12-.3.18-.07.04-.15.08-.22.13-.11.07-.22.14-.32.21-.06.04-.68.46-.68.46C6.63,3.03,3.72.81,2.55.2,1.39-.41,0,.44,0,1.75v14.25c0,8.48,6.6,15.42,14.95,15.96.05,0,.1,0,.14.01.16,0,.32.01.48.02.1,0,.2,0,.3.01.02,0,.04,0,.06,0s.04,0,.06,0c.12,0,.24,0,.36,0,.13,0,.27,0,.4-.01.14,0,.28-.02.42-.03.13,0,.27-.02.4-.03.14-.01.27-.03.41-.05.13-.02.26-.03.39-.05.14-.02.27-.05.4-.07.13-.02.26-.04.39-.07.13-.03.27-.06.4-.09.13-.03.25-.06.38-.09.13-.03.26-.07.39-.11.12-.03.25-.07.37-.1.13-.04.26-.08.38-.13.12-.04.24-.08.36-.12.13-.05.25-.1.37-.14.12-.05.24-.09.36-.14.12-.05.24-.11.37-.16.12-.05.23-.1.35-.16.12-.06.24-.12.35-.18.11-.06.23-.11.34-.17.12-.06.23-.13.34-.19.11-.06.22-.12.33-.19.11-.07.22-.14.33-.21.11-.07.21-.13.32-.2.11-.07.22-.15.32-.23.1-.07.21-.14.31-.22.11-.08.21-.16.31-.24.1-.08.2-.15.29-.23.1-.08.2-.17.3-.26.09-.08.19-.16.28-.24.1-.09.19-.18.29-.27.09-.08.18-.17.27-.26.09-.09.18-.19.28-.29.09-.09.17-.18.26-.27.09-.1.18-.2.26-.3.08-.09.16-.18.24-.28.08-.1.17-.21.25-.31.08-.1.16-.19.23-.29.08-.11.15-.22.23-.32.07-.1.15-.2.22-.3.07-.11.14-.22.22-.34.07-.1.14-.21.2-.31.07-.11.13-.23.2-.35.06-.11.13-.21.19-.32.06-.12.12-.24.19-.36.06-.11.11-.22.17-.33.06-.12.11-.25.17-.38.05-.11.1-.22.15-.33.05-.13.1-.26.15-.39.04-.11.09-.22.13-.34.05-.13.09-.27.14-.4.04-.11.08-.23.12-.34.04-.14.08-.28.12-.42.03-.11.07-.23.1-.34.04-.14.07-.29.1-.43.03-.12.06-.23.08-.35.03-.15.05-.3.08-.45.02-.11.04-.23.06-.34.02-.16.04-.32.06-.47.01-.11.03-.22.04-.33.02-.18.03-.36.04-.54,0-.09.02-.18.02-.28.01-.26.02-.52.02-.78,0-.01,0-.03,0-.04,0-8.08-6-14.77-13.79-15.84ZM25.34,13.95l-9.29,9.29c-.61.61-1.41.91-2.21.91s-1.6-.3-2.21-.91l-4.97-4.97c-1.22-1.22-1.22-3.2,0-4.42,1.22-1.22,3.2-1.22,4.42,0l2.77,2.77,7.08-7.08c1.22-1.22,3.2-1.22,4.42,0,1.22,1.22,1.22,3.2,0,4.42Z"/>
        </svg>
      </span>
    ),
  },
  telegram: {
    title: "تلگرام", label: "Telegram",
    render: (size) => (
      <span title="تلگرام" aria-label="Telegram"
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0, borderRadius: "50%", boxShadow: `0 1px 3px ${alpha(BLACK, 0.35)}`, background: `linear-gradient(135deg,${BRAND.telegram},${BRAND.telegramDeep})` }}>
        <svg width={size * 0.64} height={size * 0.64} viewBox="0 0 24 24" fill={WHITE}><path d="M9.04 15.47 8.7 19.9c.5 0 .72-.22.98-.47l2.36-2.26 4.9 3.58c.9.5 1.55.24 1.79-.83l3.23-15.12c.3-1.38-.5-1.92-1.37-1.59L1.1 9.6c-1.34.52-1.32 1.27-.23 1.6l4.92 1.54L17.2 5.9c.54-.35 1.03-.16.63.2z"/></svg>
      </span>
    ),
  },
  soroush: {
    title: "سروش‌پلاس", label: "Soroush Plus",
    render: (size) => (
      <span title="سروش‌پلاس" aria-label="Soroush Plus"
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0, borderRadius: size * 0.3, background: WHITE, boxShadow: `0 1px 3px ${alpha(BLACK, 0.35)}` }}>
        <svg width={size * 0.82} height={size * 0.82} viewBox="0 0 24 24" fill={BRAND.soroush}>
          <path d="M14.45 0.922C14.425 0.922 14.4 0.922 14.375 0.922C13.918 0.866 13.456 0.848 12.996 0.87C12.394 0.891 11.797 0.992 11.221 1.171C10.58 1.366 9.983 1.685 9.465 2.11C9.279 2.267 9.104 2.436 8.94 2.615C8.625 2.958 8.394 3.355 8.197 3.772C8.091 3.997 7.995 4.228 7.895 4.456C7.882 4.488 7.871 4.52 7.86 4.553C7.826 4.662 7.796 4.771 7.758 4.878C7.655 5.202 7.574 5.533 7.515 5.869C7.455 6.178 7.415 6.491 7.395 6.806C7.379 7.036 7.367 7.266 7.366 7.496C7.366 7.893 7.389 8.29 7.435 8.685C7.491 9.196 7.588 9.701 7.693 10.204C7.813 10.776 7.991 11.33 8.164 11.888C8.338 12.445 8.539 12.985 8.738 13.53C8.898 13.972 9.046 14.417 9.167 14.871C9.24 15.138 9.292 15.411 9.323 15.687C9.37 16.079 9.354 16.476 9.275 16.863C9.264 16.913 9.252 16.961 9.237 17.01C9.179 17.206 9.108 17.399 9.027 17.587C8.972 17.714 8.904 17.836 8.825 17.95C8.543 18.362 8.196 18.726 7.798 19.027C7.331 19.375 6.788 19.607 6.214 19.704C5.972 19.748 5.726 19.771 5.48 19.773C4.653 19.785 3.847 19.502 3.209 18.974C3.067 18.864 2.936 18.74 2.816 18.606C2.8 18.587 2.78 18.57 2.76 18.553C2.74 18.535 2.737 18.548 2.726 18.553C2.704 18.542 2.697 18.52 2.685 18.502C2.534 18.265 2.375 18.035 2.233 17.792C2.025 17.439 1.835 17.076 1.664 16.703C1.463 16.261 1.291 15.807 1.147 15.343C1.028 14.958 0.93 14.566 0.854 14.17C0.802 13.908 0.76 13.644 0.731 13.378C0.708 13.171 0.687 12.964 0.674 12.757C0.664 12.602 0.664 12.448 0.655 12.294C0.648 12.164 0.644 12.035 0.647 11.906C0.651 11.72 0.654 11.534 0.666 11.349C0.68 11.135 0.696 10.921 0.722 10.709C0.801 10.02 0.942 9.34 1.145 8.677C1.275 8.255 1.429 7.841 1.606 7.437C1.892 6.784 2.24 6.16 2.644 5.573C3.126 4.873 3.684 4.229 4.308 3.653C5.116 2.908 6.027 2.284 7.014 1.8C7.732 1.447 8.486 1.172 9.263 0.98C9.6 0.898 9.937 0.829 10.281 0.777C10.455 0.75 10.63 0.728 10.806 0.711C10.994 0.692 11.183 0.681 11.371 0.664C11.437 0.657 11.503 0.658 11.57 0.657C11.906 0.649 12.238 0.649 12.572 0.661C12.701 0.666 12.83 0.681 12.959 0.691C13.034 0.697 13.109 0.704 13.183 0.711C13.453 0.737 13.721 0.774 13.987 0.824C14.123 0.849 14.259 0.876 14.395 0.902C14.415 0.902 14.435 0.909 14.45 0.922Z" />
          <path d="M5.259 21.117C5.28 21.122 5.299 21.132 5.315 21.147C5.633 21.364 5.965 21.559 6.309 21.733C6.929 22.065 7.581 22.332 8.257 22.531C8.68 22.653 9.111 22.747 9.548 22.811C10.145 22.898 10.748 22.926 11.35 22.897C11.725 22.88 12.099 22.84 12.469 22.776C12.677 22.74 12.883 22.698 13.089 22.651C13.655 22.52 14.208 22.341 14.743 22.116C15.2 21.927 15.644 21.706 16.07 21.453C16.596 21.144 17.068 20.751 17.466 20.289C17.867 19.834 18.225 19.344 18.536 18.823C19.012 18.011 19.35 17.125 19.536 16.201C19.598 15.898 19.642 15.592 19.668 15.284C19.699 14.938 19.709 14.591 19.697 14.244C19.662 13.196 19.449 12.161 19.066 11.184C18.992 10.993 18.913 10.804 18.835 10.614C18.819 10.576 18.801 10.54 18.782 10.502C18.4 9.779 17.929 9.107 17.378 8.502C17.021 8.105 16.633 7.74 16.245 7.375C15.964 7.113 15.666 6.877 15.366 6.641C15.155 6.473 14.938 6.318 14.736 6.141C14.671 6.077 14.601 6.018 14.527 5.965C14.503 5.948 14.482 5.929 14.463 5.908C14.372 5.813 14.278 5.719 14.19 5.621C13.905 5.309 13.713 4.924 13.637 4.508C13.56 4.05 13.605 3.579 13.768 3.144C14.035 2.46 14.521 2.003 15.206 1.757C15.662 1.589 16.158 1.557 16.631 1.667C16.659 1.673 16.687 1.677 16.715 1.681C16.716 1.683 16.718 1.685 16.72 1.686C16.721 1.687 16.724 1.688 16.726 1.688C16.728 1.689 16.73 1.688 16.733 1.688C16.735 1.687 16.737 1.686 16.738 1.685C16.804 1.706 16.867 1.735 16.926 1.771C17.609 2.101 18.258 2.499 18.863 2.958C19.119 3.152 19.366 3.358 19.605 3.575C19.858 3.804 20.1 4.043 20.332 4.293C21.581 5.64 22.484 7.271 22.961 9.045C23.048 9.372 23.12 9.701 23.18 10.032C23.215 10.228 23.243 10.424 23.265 10.621C23.288 10.831 23.309 11.041 23.322 11.252C23.331 11.383 23.335 11.516 23.339 11.649C23.342 11.761 23.351 11.869 23.349 11.979C23.346 12.2 23.344 12.421 23.33 12.641C23.315 12.886 23.293 13.13 23.264 13.375C23.098 14.765 22.675 16.111 22.017 17.346C21.835 17.689 21.634 18.021 21.414 18.341C21.396 18.366 21.383 18.395 21.375 18.425C21.368 18.456 21.367 18.487 21.373 18.518C21.397 18.677 21.419 18.836 21.441 18.995C21.462 19.155 21.484 19.32 21.505 19.482C21.528 19.657 21.551 19.832 21.573 20.006C21.589 20.129 21.602 20.25 21.617 20.372C21.633 20.507 21.651 20.641 21.667 20.775C21.684 20.91 21.698 21.032 21.713 21.16C21.728 21.288 21.741 21.404 21.756 21.526C21.772 21.657 21.789 21.789 21.805 21.919C21.823 22.063 21.841 22.207 21.858 22.351C21.873 22.476 21.887 22.6 21.902 22.726C21.91 22.791 21.918 22.857 21.926 22.923C21.934 22.988 21.918 23.006 21.846 22.988C21.785 22.973 21.724 22.956 21.664 22.939L20.249 22.561C19.8 22.442 19.354 22.323 18.907 22.202C18.566 22.111 18.226 22.017 17.886 21.922C17.822 21.904 17.758 21.888 17.695 21.866C17.677 21.859 17.659 21.857 17.64 21.859C17.621 21.861 17.604 21.868 17.588 21.878C17.348 22.017 17.099 22.142 16.849 22.262C16.493 22.431 16.128 22.581 15.757 22.711C15.361 22.85 14.958 22.966 14.55 23.06C14.301 23.116 14.051 23.169 13.799 23.204C13.64 23.226 13.481 23.255 13.322 23.272C13.147 23.291 12.971 23.307 12.795 23.319C12.467 23.34 12.14 23.354 11.812 23.348C11.645 23.345 11.477 23.338 11.311 23.329C11.16 23.32 11.009 23.309 10.858 23.292C10.643 23.267 10.427 23.241 10.213 23.207C9.967 23.167 9.723 23.122 9.483 23.067C8.85 22.923 8.23 22.724 7.631 22.475C7.29 22.332 6.957 22.173 6.632 21.999C6.174 21.752 5.733 21.474 5.313 21.167C5.29 21.155 5.265 21.145 5.259 21.117Z" />
        </svg>
      </span>
    ),
  },
  eitaa: {
    title: "ایتا", label: "Eitaa",
    render: (size) => (
      <span title="ایتا" aria-label="Eitaa"
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>
        {/* drop-shadow (not box-shadow) so the glow follows the squircle, not a square span */}
        <svg width={size} height={size} viewBox="0 0 3584.55 3673.6" style={{ filter: `drop-shadow(0 1px 2px ${alpha(BLACK, 0.35)})` }}>
          <rect x="0" y="0" width="3584.55" height="3673.6" rx="1024" ry="1024" fill={WHITE} />
          <path fill={BRAND.eitaa} fillRule="evenodd" transform="translate(-47.27 -2.74)" d="M1071.43,2.75H2607.66C3171,2.75,3631.82,462.91,3631.82,1026.2v493.93c-505,227-1014.43,1348.12-1756.93,1104.51-61.16,43.46-202.11,222.55-212,358.43-257.11-34.24-553.52-328.88-517.95-646.62C717,2026.91,1070.39,1455.5,1409.74,1225.51c727.32-492.94,1737.05-69,1175.39,283.45-341.52,214.31-1071.84,355.88-995.91-170.24-200.34,57.78-328.58,431.34-87.37,626-223.45,219.53-180.49,623.07,58.36,755.57,241.56-625.87,1082.31-544.08,1422-1291.2,255.57-562-123.34-1202.37-880.91-1104C1529.56,399.34,993.64,881.63,725.62,1453.64,453.68,2034,494.15,2811.15,1052.55,3202.82c657.15,460.92,1356.78,34.13,1780.52-523.68,249.77-328.78,468-693,798.75-903.37v875.72c0,563.28-460.88,1024.86-1024.16,1024.86H1071.43c-563.29,0-1024.16-460.87-1024.16-1024.16V1026.9C47.27,463.61,508.14,2.74,1071.43,2.74Z" />
        </svg>
      </span>
    ),
  },
  // Cafe Bazaar — the Android app. Bazaar's OWN app icon (the green bag), verbatim
  // from webassets.cafebazaar.ir/images/icons/android-icon-96x96.png (2026-10-29),
  // inlined as a data URI for the same reason the SVG marks are inline. `pickable:
  // false` — a Bazaar user exists only by signing in from the app (an account id
  // the admin can't know), so «افزودن کاربر» doesn't offer it.
  bazaar: {
    title: "بازار", label: "Bazaar", pickable: false,
    render: (size) => (
      <span title="بازار" aria-label="Bazaar"
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>
        <img src={BAZAAR_ICON} width={size} height={size} alt="" style={{ display: "block" }} />
      </span>
    ),
  },
};


export function PlatformBadge({ platform, size = 18 }) {
  // Unknown/empty platform → Bale, which is the backend's DEFAULT_PLATFORM too.
  return (PLATFORM_MARKS[platform] || PLATFORM_MARKS.bale).render(size);
}

// ── Loading spinner ──────────────────────────────────────────────────────────
// Spark emitters at FIXED world angles; each fires when the rotating head passes
// it, so sparks detach from the head, drift outward, and burn out (see Onboarding).
export const SPARK_EMITTERS = Array.from({ length: 16 }, (_, i) => {
  const rnd = (k) => { const x = Math.sin((i + 1) * k) * 43758.5453; return x - Math.floor(x); };
  return {
    ang: i * 22.5,
    sparks: [
      { jx: (rnd(12.9898) - 0.5) * 0.3, fly: -(0.16 + rnd(78.233) * 0.24), s: 0.6 + rnd(37.719) * 0.7, dt: 0 },
      { jx: (rnd(51.7) - 0.5) * 0.3, fly: -(0.13 + rnd(91.3) * 0.22), s: 0.55 + rnd(7.77) * 0.65, dt: 0.06 },
    ],
  };
});

// ── Squircle (superellipse) — Apple/One-UI smooth corners for SQUARE surfaces.
// Mirrors the MiniApp implementation; drawn as an SVG path (CSS corner-shape
// rendered sharp on-device). Normalized 0–100 viewBox scales to any size; the
// inset keeps the non-scaling stroke off the edges so it's never clipped.
export function buildSquirclePath(n = 3, steps = 88, inset = 4) {
  const a = 50 - inset, c = 50, pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    const ct = Math.cos(t), st = Math.sin(t);
    const x = c + Math.sign(ct) * a * Math.pow(Math.abs(ct), 2 / n);
    const y = c + Math.sign(st) * a * Math.pow(Math.abs(st), 2 / n);
    pts.push((i === 0 ? "M" : "L") + x.toFixed(2) + " " + y.toFixed(2));
  }
  return pts.join(" ") + " Z";
}
export const SQUIRCLE_D = buildSquirclePath(3);
export function SquircleBg({ fill = "none", stroke = "none", strokeWidth = 0, glow }) {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%" aria-hidden
      style={{ position: "absolute", inset: 0, overflow: "visible", filter: glow ? `drop-shadow(${glow})` : undefined, pointerEvents: "none" }}>
      <path d={SQUIRCLE_D} fill={fill} stroke={stroke} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Magical spinner — a gentle glowing head circles the ring, shedding sparks that
// detach in world space, scatter outward, and burn out.
export function Spinner({ size = 24, color }) {
  const c = color || C.gold;
  const ring = Math.max(2, Math.round(size * 0.1));
  const head = Math.max(3, Math.round(size * 0.16));
  const dur = 1.8;
  const maskR = `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), ${BLACK} calc(100% - ${ring}px))`;
  return (
    <span style={{ position: "relative", display: "inline-block", width: size, height: size, verticalAlign: "middle" }}>
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `conic-gradient(from 0deg, ${c} 0deg, ${c}00 200deg, ${c}00 360deg)`, WebkitMask: maskR, mask: maskR, animation: `magspin ${dur}s linear infinite` }} />
      <span style={{ position: "absolute", inset: 0, animation: `magspin ${dur}s linear infinite` }}>
        <span style={{ position: "absolute", top: -head * 0.08, left: "50%", width: head, height: head, marginLeft: -head / 2, borderRadius: "50%", background: WHITE, boxShadow: `0 0 ${Math.round(size * 0.3)}px ${c}, 0 0 ${Math.round(size * 0.13)}px ${WHITE}` }} />
      </span>
      {SPARK_EMITTERS.map((e, i) => {
        // magspin is counterclockwise; the head passes world angle `ang` at
        // t = dur·((360−ang)%360)/360 — that's this emitter's firing delay.
        const base = dur * (((360 - e.ang) % 360) / 360);
        return (
          <span key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${e.ang}deg)`, pointerEvents: "none" }}>
            {e.sparks.map((sp, j) => {
              const d = Math.max(1.5, size * 0.09 * sp.s);
              return (
                <span key={j} style={{ position: "absolute", top: 0, left: "50%", width: d, height: d, marginLeft: -d / 2, borderRadius: "50%", background: WHITE, opacity: 0, boxShadow: `0 0 ${Math.round(size * 0.1)}px ${c}`, "--jx": `${(sp.jx * size).toFixed(1)}px`, "--fly": `${(sp.fly * size).toFixed(1)}px`, animation: `magfly ${dur}s linear ${(base + sp.dt).toFixed(2)}s infinite` }} />
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

// ── Shimmer loading placeholder ─────────────────────────────────────────────
export function Shimmer({ width = "100%", height = 20, borderRadius = 8 }) {
  return (
    <div style={{
      width, height, borderRadius,
      background: `linear-gradient(90deg, ${C.bgCard} 25%, ${C.bgHover} 50%, ${C.bgCard} 75%)`,
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s ease-in-out infinite",
    }} />
  );
}


// ── Filter pills beside a search field ───────────────────────────────────────
// Single choice; items: [{ key, label, color? }]. The ONE look for every page's
// filter row («کودکان و اشتراک‌ها», «پرسشنامه‌ها», …) — selection is colour only.
// It may SHRINK (and so wrap its own chips): with flexShrink 0 it kept its one-line
// width on a phone and made the whole page scroll sideways.
export function FilterChips({ value, onChange, items }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: "0 1 auto", minWidth: 0, maxWidth: "100%" }}>
      {items.map(({ key, label, color = C.gold, icon }) => {
        const on = value === key;
        return (
          <button key={key} className="press" onClick={() => onChange(key)}
            style={{ background: on ? `${color}1F` : C.bgCard, border: `1px solid ${on ? color : C.border}`, color: on ? color : C.textMid, borderRadius: 10, padding: "8px 16px", fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 7 }}>
            {icon}{label}
          </button>
        );
      })}
    </div>
  );
}

// ── Jalali date picker ───────────────────────────────────────────────────────
// value/onChange: "YYYY-MM-DD" (Jalali). Day · month · year selects — the admin
// thinks in Jalali, and a native <input type=date> would hand them Gregorian.
export function tehranTodayJalali(addDays = 0) {
  const d = new Date(Date.now() + addDays * 864e5);
  const [y, m, dd] = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(d).split("-").map(Number);
  const j = greToJalaliFull(y, m, dd);
  return `${j.jy}-${String(j.jm).padStart(2, "0")}-${String(j.jd).padStart(2, "0")}`;
}

export function JalaliDateField({ value, onChange }) {
  const [jy, jm, jd] = (value || tehranTodayJalali()).split("-").map(Number);
  const thisYear = Number(tehranTodayJalali().slice(0, 4));
  const set = (y, m, d) => {
    const dd = Math.min(d, jalaliMonthDays(y, m));
    onChange(`${y}-${String(m).padStart(2, "0")}-${String(dd).padStart(2, "0")}`);
  };
  const sel = { flex: 1, minWidth: 0, background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 8px", color: C.cream, fontFamily: FONT, fontSize: 14, outline: "none", cursor: "pointer" };
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <select value={jd} onChange={e => set(jy, jm, Number(e.target.value))} style={sel} aria-label="روز">
        {Array.from({ length: jalaliMonthDays(jy, jm) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{fmt(d)}</option>)}
      </select>
      <select value={jm} onChange={e => set(jy, Number(e.target.value), jd)} style={{ ...sel, flex: 1.6 }} aria-label="ماه">
        {JALALI_MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
      </select>
      <select value={jy} onChange={e => set(Number(e.target.value), jm, jd)} style={sel} aria-label="سال">
        {[thisYear, thisYear + 1].map(y => <option key={y} value={y}>{toPersianDigits(y)}</option>)}
      </select>
    </div>
  );
}

// ── Sort picker: field + direction ────────────────────────────────────────────
// options: [{ key, label }]; `sort` = { key, dir: "asc"|"desc" } from useSort().
export function SortControl({ sort, setSort, options }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 11, padding: 3, gap: 2, flexShrink: 0 }}>
      <select value={sort.key} onChange={e => setSort({ key: e.target.value })} aria-label="مرتب‌سازی"
        style={{ background: "transparent", border: "none", color: C.cream, fontFamily: FONT, fontSize: 13, fontWeight: 600, padding: "6px 8px", outline: "none", cursor: "pointer" }}>
        {options.map(o => <option key={o.key} value={o.key} style={{ background: C.bgCard }}>مرتب‌سازی: {o.label}</option>)}
      </select>
      <button className="press" onClick={() => setSort({ dir: sort.dir === "asc" ? "desc" : "asc" })}
        title={sort.dir === "asc" ? "صعودی" : "نزولی"} aria-label="جهت مرتب‌سازی"
        style={{ background: `${C.gold}1F`, border: "none", color: C.gold, borderRadius: 8, padding: "7px 9px", cursor: "pointer", display: "flex", alignItems: "center" }}>
        {sort.dir === "asc" ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
      </button>
    </div>
  );
}

// Small list/card switcher shown in a page header
export function ViewToggle({ view, setView }) {
  const btn = (mode, Icon, label) => {
    const on = view === mode;
    return (
      <button className="press" onClick={() => setView(mode)} title={label} aria-label={label}
        style={{ background: on ? `${C.gold}1F` : "transparent", border: "none", color: on ? C.gold : C.textMid, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", transition: "all 0.15s" }}>
        <Icon size={17} />
      </button>
    );
  };
  return (
    <div style={{ display: "inline-flex", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 11, padding: 3, gap: 2, flexShrink: 0 }}>
      {btn("list", List, "نمای فهرستی")}
      {btn("card", LayoutGrid, "نمای کارتی")}
    </div>
  );
}

// Real tab bar: items are always visible side by side and exactly one is active
// at a time, so switching to another tab closes the previous one automatically
// — no separate "close" state/label needed. Same pill-button shape as the
// rest of the DS (dark rounded track holding gold-tinted pill buttons), just
// grouped into one component instead of duplicated inline per page.
// items: [{ key, label }]. Use this instead of independent expand/collapse
// toggle buttons whenever the sections underneath are mutually exclusive.
//
// Deliberately does NOT change font-weight or add horizontal scroll on select:
// - font-weight was the earlier bug — bolding the active label widened it just
//   enough to flip wrapping right at the edge, and the squircle engine's
//   clip-path (sized off the old box) lagged a frame behind the resize,
//   showing native square corners / a stray scrollbar until it caught up.
// - border color always resolves to a real color (never literal "transparent")
//   — the squircle engine treats an inline "transparent" as its OWN blanked
//   sentinel and won't cache a new color over it, which left a deselected tab
//   stuck showing its old gold border forever.
// Selection is color/background only, so the box never resizes and the
// engine's geometry never has anything to race.
export function Tabs({ value, onChange, items, style }) {
  return (
    <div style={{ display: "inline-flex", flexWrap: "wrap", width: "fit-content", maxWidth: "100%", background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, gap: 4, ...style }}>
      {items.map(it => {
        const on = value === it.key;
        return (
          <button key={it.key} className="press" onClick={() => onChange(it.key)}
            style={{
              background: on ? `${C.gold}18` : "transparent", border: `1px solid ${on ? C.gold : C.border}`,
              color: on ? C.gold : C.textMid, fontFamily: FONT, fontSize: 13, fontWeight: 600,
              borderRadius: 9, padding: "7px 14px", cursor: "pointer", whiteSpace: "nowrap",
              transition: "background 0.15s, border-color 0.15s, color 0.15s",
            }}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// Shared search input — one source of truth so its height/style can't drift
// between pages (Children, Questionnaires, …).
export function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{ flex: "1 1 220px", minWidth: 0, position: "relative" }}>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", color: C.cream, fontFamily: FONT, fontSize: 14.5, outline: "none", boxSizing: "border-box" }} />
      {value && (
        <button className="press" onClick={() => onChange("")}
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMid, cursor: "pointer", padding: 4 }}>
          <X size={15} />
        </button>
      )}
    </div>
  );
}

// ── Error banner ─────────────────────────────────────────────────────────────
export function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}44`, borderRadius: 14, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
      <span style={{ color: C.red, fontSize: 14, lineHeight: 1.7 }}>خطا: {message}</span>
      {onRetry && (
        <button className="press" onClick={onRetry}
          style={{ background: `${C.red}22`, border: `1px solid ${C.red}55`, color: C.red, borderRadius: 9, padding: "7px 16px", fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
          تلاش مجدد
        </button>
      )}
    </div>
  );
}


// The sentinel at the foot of a lazy list (useIncremental in hooks.js): it loads
// the next batch as it nears the viewport, and is a plain button as a fallback.
export function LoadMore({ sentinelRef, hasMore, onMore, shown, total }) {
  if (!hasMore) return null;
  return (
    <div ref={sentinelRef} style={{ display: "flex", justifyContent: "center", padding: "18px 0", gridColumn: "1 / -1" }}>
      <Btn variant="outline" color={C.textMid} onClick={onMore}>
        نمایش بیشتر ({fmt(shown)} از {fmt(total)})
      </Btn>
    </div>
  );
}

export function Badge({ children, color = C.teal }) {
  return (
    <span style={{ background: `${color}22`, color, border: `1px solid ${color}44`, borderRadius: 999, padding: "3px 12px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", display: "inline-block" }}>
      {children}
    </span>
  );
}

// ── Unified button — ALL buttons share these exact dimensions/typography.
// variant="gold" is the primary (gradient, dark text); variant="outline" keeps the
// same shape but takes its color from `color` (usage-dependent). Full-width via style.
export function Btn({ variant = "gold", color, children, style, ...props }) {
  const c = color || C.gold;
  const base = { height: 38, padding: "0 16px", borderRadius: 12, fontFamily: FONT, fontSize: 13.5, fontWeight: 700, cursor: props.disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.15s", opacity: props.disabled ? 0.55 : 1 };
  const look = variant === "gold"
    ? { background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: "none", color: C.bgDeep }
    : { background: `${c}14`, border: `1px solid ${c}44`, color: c };
  return <button className="press" {...props} style={{ ...base, ...look, ...style }}>{children}</button>;
}

// ── Unified modal — backdrop + card + title row + close. Any new dialog uses this
// (bottom-sheet on mobile, centered card on desktop). Never hand-roll a backdrop.
export function Modal({ title, onClose, children, isMobile, maxWidth = 440 }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: alpha(BLACK, 0.72), zIndex: 120, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 24, animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 22, width: "100%", maxWidth, maxHeight: "88vh", overflowY: "auto", boxShadow: `0 20px 60px ${alpha(BLACK, 0.6)}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 10 }}>
          <div style={{ color: C.gold, fontSize: 16, fontWeight: 700 }}>{title}</div>
          <button className="press" onClick={onClose} aria-label="بستن"
            style={{ background: "transparent", border: "none", color: C.textMid, fontSize: 24, lineHeight: 1, cursor: "pointer", padding: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

